import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Layers,
  Cog,
  BarChart3,
  Award,
  Cpu,
  Factory,
  HardHat,
  Warehouse,
  Users,
  Calendar,
  Settings,
  Package,
  ShieldCheck,
  Wrench,
  Zap,
  Clock,
} from "lucide-react";
import { useHomeCta } from "../hooks/useHomeCta";
import { openAgent } from "../utils/openAgent";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";

const OVERVIEW_CARDS = [
  {
    icon: Layers,
    iconColor: "#2563EB",
    iconBg: "#E0F2FE",
    title: "Unified Manufacturing Operations",
    desc: "Connect production planning, procurement, inventory, quality control and shop floor processes into one intelligent platform for seamless, end-to-end management.",
  },
  {
    icon: Cog,
    iconColor: "#9333EA",
    iconBg: "#F3E8FF",
    title: "AI-Powered Process Automation",
    desc: "Automate repetitive workflows, optimize production schedules, predict equipment maintenance, and improve operational efficiency using AI-driven insights.",
  },
  {
    icon: BarChart3,
    iconColor: "#16A34A",
    iconBg: "#DCFCE7",
    title: "Real-Time Visibility & Decision Making",
    desc: "Monitor production performance, machine health, inventory levels and operational KPIs through centralized dashboards for faster, data-driven decisions.",
  },
];

const STACK_LAYERS = [
  {
    title: "Auto Decisioning & Predictive Analytics",
    bg: "#FA8142",
    role: "Top Management",
    roleIcon: Award,
  },
  {
    title: "Enterprise Resource Planning (ERP)",
    bg: "#45B667",
    role: "Functional & Middle Management",
    roleIcon: Cpu,
  },
  {
    title: "Manufacturing Execution System (MES)",
    bg: "#0EA5E9",
    role: "Factory Management",
    roleIcon: Factory,
  },
  {
    title: "SCADA/IIOT Platform",
    bg: "#0284C7",
    role: "Factory Supervisors",
    roleIcon: HardHat,
  },
  {
    title: "IIOT / Sensors / RFID / Camera",
    bg: "#1E3A8A",
    role: "Shop Floor",
    roleIcon: Warehouse,
  },
  {
    title: "Shopfloor Apps",
    bg: "#7C3AED",
    role: "Shop Floor Team",
    roleIcon: Users,
  },
];

const KEY_BENEFITS = [
  {
    icon: Calendar,
    iconColor: "#2563EB",
    title: "Production Planning",
    desc: "AI-optimized scheduling that adapts to real-time demand, capacity and machine availability.",
  },
  {
    icon: Settings,
    iconColor: "#EA580C",
    title: "Shop Floor Automation",
    desc: "Direct integration with PLC, SCADA and robotics for touchless floor control.",
  },
  {
    icon: Package,
    iconColor: "#16A34A",
    title: "Inventory Management",
    desc: "Live tracking of work-in-progress and raw materials with automated recording and alerts.",
  },
  {
    icon: ShieldCheck,
    iconColor: "#6366F1",
    title: "Quality Control",
    desc: "Computer vision and AI models to detect defects and ensure consistent quality.",
  },
  {
    icon: Wrench,
    iconColor: "#D946EF",
    title: "Predictive Maintenance",
    desc: "Predict equipment failures with advanced ML models and reduce downtime.",
  },
  {
    icon: BarChart3,
    iconColor: "#EAB308",
    title: "AI Analytics",
    desc: "Deep learning models to uncover hidden bottlenecks and optimize processes.",
  },
];

const IMPACT_STATS = [
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#E0F7FA] border border-[#BAE6FD]">
        <Zap className="w-5 h-5 text-[#00B4D8]" strokeWidth={2.2} />
      </div>
    ),
    stat: "20–40%",
    label: "Higher productivity",
  },
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#F3E8FF] border border-[#E9D5FF]">
        <Settings className="w-5 h-5 text-[#A855F7]" strokeWidth={2.2} />
      </div>
    ),
    stat: "15–30%",
    label: "Lower operational costs",
  },
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#DBEAFE] border border-[#BFDBFE]">
        <Clock className="w-5 h-5 text-[#3B82F6]" strokeWidth={2.2} />
      </div>
    ),
    stat: "3–5x",
    label: "Faster decision making",
  },
  {
    icon: (
      <div className="w-11 h-11 shrink-0 rounded-[12px] flex items-center justify-center bg-[#FEF3C7] border border-[#FDE68A]">
        <svg
          className="w-5 h-5 text-[#F59E0B]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="4" />
          <path d="M7 16v-3" />
          <path d="M12 16v-6" />
          <path d="M17 16v-9" />
        </svg>
      </div>
    ),
    stat: "25–50%",
    label: "Reduction in downtime",
  },
];

const SUCCESS_METRICS = [
  {
    label: "Inventory Accuracy",
    value: "85%",
    percentage: 85,
  },
  {
    label: "Quality Improvement",
    value: "50%",
    percentage: 50,
  },
  {
    label: "Less Downtime",
    value: "70%",
    percentage: 70,
  },
];

const MANUFACTURING_PARTNERS = [
  {
    name: "OpenAI",
    img: "/images/labs/openai.png",
    width: "134px",
    height: "40px",
  },
  {
    name: "AWS",
    img: "/images/labs/aws.png",
    width: "51px",
    height: "30px",
  },
  {
    name: "NVIDIA",
    img: "/images/labs/nvidia.png",
    width: "164px",
    height: "30px",
  },
  {
    name: "Microsoft Azure",
    img: "/images/labs/azure.png",
    width: "215.88px",
    height: "30px",
    gap: "7.75px",
    flow: "horizontal",
  },
  {
    name: "Meta",
    img: "/images/labs/meta.png",
    width: "135px",
    height: "30px",
  },
  {
    name: "Anthropic",
    img: "/images/labs/Anthropic.png",
    width: "87px",
    height: "16px",
    flow: "vertical",
  },
];

const ManufacturingStackPage = () => {
  const navigate = useNavigate();
  const goToHomeCta = useHomeCta();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-[#020B16] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black">
      {/* Stack Navbar matching screenshot */}
      <StackNavbar />

      {/* Main Hero Section with Background Video */}
      <section className="relative w-full min-h-screen flex items-center overflow-hidden pt-[76px] sm:pt-[82px]">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover object-center"
          >
            <source src="/videos/manufacturing-stack-hero.mp4" type="video/mp4" />
          </video>

          {/* Left-to-right dark gradient overlay matching screenshot */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #020B16 0%, rgba(2, 11, 22, 0.98) 32%, rgba(2, 11, 22, 0.88) 46%, rgba(2, 11, 22, 0.35) 68%, rgba(2, 11, 22, 0.08) 85%, transparent 100%)",
            }}
          />

          {/* Top and Bottom soft dark fades */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(2, 11, 22, 0.5) 0%, transparent 20%, transparent 80%, rgba(2, 11, 22, 0.7) 100%)",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-20 lg:px-[70px]">
          <div className="max-w-[580px] flex flex-col items-start">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[10.5px] sm:text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#7E93AC] select-none">
              <Link to="/what-we-do" className="hover:text-white transition-colors">
                WHAT WE DO
              </Link>
              <span>&gt;</span>
              <Link to="/what-we-do#stacks" className="hover:text-white transition-colors">
                STACK
              </Link>
              <span>&gt;</span>
              <span className="text-[#A2B6CE]">MANUFACTURING AUTOMATION STACK</span>
            </div>

            {/* Main Heading */}
            <h1 className="mt-5 text-[40px] sm:text-[50px] lg:text-[58px] xl:text-[62px] font-extrabold leading-[1.08] tracking-tight">
              <span className="block text-white">Manufacturing</span>
              <span className="block bg-gradient-to-r from-[#00D2EE] to-[#38BDF8] bg-clip-text text-transparent">
                Automation Stacks
              </span>
            </h1>

            {/* Tagline / Subtitle */}
            <p className="mt-5 text-[16px] sm:text-[17.5px] font-bold leading-snug text-white">
              Intelligent automation for modern manufacturing enterprises.
            </p>

            {/* Description */}
            <p className="mt-3.5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[26px] text-[#9FB3C8] max-w-[520px]">
              Unify your shop floor, systems, data and people with AI-powered automation stacks. Optimize production, automate operations, and gain real-time visibility across your manufacturing ecosystem.
            </p>

            {/* CTA Button */}
            <div className="mt-8 sm:mt-9">
              <button
                onClick={openAgent}
                className="group inline-flex items-center gap-3 rounded-full bg-white px-7 sm:px-8 py-3.5 sm:py-4 text-[14px] sm:text-[15px] font-semibold text-[#04101E] transition-all duration-300 hover:scale-[1.03] active:scale-95 cursor-pointer"
                style={{
                  boxShadow: "0 0 28px 2px rgba(0, 210, 238, 0.45), 0 4px 18px rgba(0, 0, 0, 0.3)",
                }}
              >
                <span>Talk to our team</span>
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                  strokeWidth={2.4}
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Overview Section matching Screenshots 1 & 2 */}
      <section
        className="w-full py-16 sm:py-20 lg:py-24 border-b border-[#E2E8F0]"
        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-14">
            {/* Left Content */}
            <div className="flex flex-col items-start max-w-[560px]">
              <p className="text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                PLATFORM OVERVIEW
              </p>

              <h2 className="mt-4 text-[32px] sm:text-[38px] lg:text-[42px] font-extrabold leading-[1.14] tracking-tight text-[#0F172A]">
                <span className="block whitespace-normal sm:whitespace-nowrap">
                  Transform Manufacturing with
                </span>
                <span className="block bg-gradient-to-r from-[#00D2EE] to-[#0284C7] bg-clip-text text-transparent">
                  Intelligent Automation
                </span>
              </h2>

              <p className="mt-4 text-[14px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#64748B] max-w-[460px]">
                Our Manufacturing Automation Stacks connect your people, processes and data to drive operational excellence across the entire production lifecycle.
              </p>
            </div>

            {/* Right Cards: 3 vertical cards side-by-side matching Screenshot 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
              {OVERVIEW_CARDS.map((card) => {
                const Icon = card.icon;
                return (
                  <div
                    key={card.title}
                    className="flex flex-col items-center text-center w-full sm:w-[223px] min-h-[255px] rounded-[12px] p-4 sm:p-[18px] bg-white transition-all duration-300 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:-translate-y-1 select-none"
                    style={{
                      border: "1px solid rgba(230, 230, 230, 1)",
                      gap: "16px",
                    }}
                  >
                    {/* Icon Badge */}
                    <div
                      className="flex h-[46px] w-[46px] items-center justify-center rounded-full shrink-0"
                      style={{ backgroundColor: card.iconBg }}
                    >
                      <Icon
                        className="h-5 w-5"
                        style={{ color: card.iconColor }}
                        strokeWidth={2.2}
                      />
                    </div>

                    {/* Title */}
                    <h3 className="text-[15px] sm:text-[15.5px] font-bold text-[#0F172A] leading-snug">
                      {card.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[11px] sm:text-[11.5px] leading-[18px] text-[#64748B]">
                      {card.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Transforming Operations Section matching Screenshot */}
      <section className="relative w-full overflow-hidden py-20 sm:py-24 lg:py-28 bg-[#000814]">
        {/* Background Image provided by user */}
        <img
          src="/images/what-we-do/manufacturing-operations-bg.png"
          alt="Transforming Operations Background"
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none select-none opacity-85"
        />

        {/* Subtle dark gradient overlay for optimal text contrast */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(0, 8, 20, 0.95) 0%, rgba(0, 8, 20, 0.78) 35%, rgba(0, 8, 20, 0.3) 65%, rgba(0, 8, 20, 0.6) 100%)",
          }}
        />

        {/* Content Container */}
        <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="flex flex-col xl:flex-row items-center justify-between gap-12 xl:gap-14">
            
            {/* Left Headline */}
            <div className="flex flex-col items-start max-w-[480px] shrink-0">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.6px] text-[#7E93AC]">
                TRANSFORMING OPERATIONS
              </p>

              <h2 className="mt-4 text-[34px] sm:text-[40px] lg:text-[44px] font-extrabold leading-[1.15] tracking-tight text-white">
                <span className="block whitespace-normal sm:whitespace-nowrap">From data to decisions,</span>
                <span className="block text-white">across your factory.</span>
              </h2>

              <p className="mt-4 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#9FB3C8] max-w-[440px]">
                Our automation Stack integrates AI, IoT, and<br className="hidden sm:block" />
                enterprise systems to create a connected<br className="hidden sm:block" />
                manufacturing ecosystem.
              </p>
            </div>

            {/* Right Connected Architecture Diagram */}
            <div className="w-full xl:w-auto overflow-x-auto pb-4 xl:pb-0 flex justify-center">
              <div className="min-w-[620px] sm:min-w-[660px] flex items-center justify-center gap-2.5 sm:gap-3 py-4 select-none">
                
                {/* 1. Algorithm Layers Pillar (Green) */}
                <div
                  className="w-[28px] h-[256px] rounded-[8px] flex items-center justify-center shrink-0 shadow-md transition-transform duration-300 hover:scale-[1.02]"
                  style={{ backgroundColor: "#74D49A" }}
                >
                  <span
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    className="text-[11px] font-bold tracking-wider text-[#0B1528] whitespace-nowrap"
                  >
                    Algorithm Layers
                  </span>
                </div>

                {/* Dotted Arrow between Pillar 1 and Pillar 2 */}
                <div className="w-[18px] flex items-center justify-center shrink-0">
                  <svg className="w-4 h-3.5" viewBox="0 0 16 14" fill="none">
                    <line x1="2" y1="7" x2="14" y2="7" stroke="white" strokeWidth="1.2" strokeDasharray="1.5 1.5" strokeOpacity="0.8" />
                    <polyline points="5 4, 2 7, 5 10" stroke="white" strokeWidth="1.2" strokeOpacity="0.8" />
                    <polyline points="11 4, 14 7, 11 10" stroke="white" strokeWidth="1.2" strokeOpacity="0.8" />
                  </svg>
                </div>

                {/* 2. Unifies Data Platform Pillar (Cyan) */}
                <div
                  className="w-[28px] h-[256px] rounded-[8px] flex items-center justify-center shrink-0 shadow-md transition-transform duration-300 hover:scale-[1.02]"
                  style={{ backgroundColor: "#00D2EE" }}
                >
                  <span
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    className="text-[11px] font-bold tracking-wider text-[#0B1528] whitespace-nowrap"
                  >
                    Unifies Data Platform
                  </span>
                </div>

                {/* Middle Column: 6 Stack Bars + Arrows */}
                <div className="flex flex-col gap-[9px] shrink-0">
                  {STACK_LAYERS.map((layer) => (
                    <div key={layer.title} className="flex items-center gap-2">
                      {/* Left bidirectional dashed arrow from Pillar 2 to Bar */}
                      <svg className="w-5 sm:w-6 h-3 shrink-0" viewBox="0 0 24 12" fill="none">
                        <line x1="2" y1="6" x2="22" y2="6" stroke="white" strokeWidth="1.2" strokeDasharray="1.5 1.5" strokeOpacity="0.75" />
                        <polyline points="5 3, 2 6, 5 9" stroke="white" strokeWidth="1.2" strokeOpacity="0.75" />
                        <polyline points="19 3, 22 6, 19 9" stroke="white" strokeWidth="1.2" strokeOpacity="0.75" />
                      </svg>

                      {/* Stack Bar Pill */}
                      <div
                        className="w-[195px] sm:w-[210px] h-[34px] rounded-[8px] px-3 flex items-center justify-center text-center text-white text-[11px] sm:text-[11.5px] font-semibold leading-tight shadow-sm transition-all duration-300 hover:scale-[1.02] hover:brightness-105 shrink-0"
                        style={{ backgroundColor: layer.bg }}
                      >
                        <span className="truncate">{layer.title}</span>
                      </div>

                      {/* Right curved dashed arrow from Bar to Role */}
                      <svg className="w-8 sm:w-10 h-4 shrink-0" viewBox="0 0 40 16" fill="none">
                        <path
                          d="M2 8 C15 8, 25 8, 36 8"
                          stroke="white"
                          strokeWidth="1.2"
                          strokeDasharray="2 2"
                          strokeOpacity="0.65"
                        />
                        <polyline
                          points="32 5, 36 8, 32 11"
                          stroke="white"
                          strokeWidth="1.2"
                          strokeOpacity="0.75"
                        />
                      </svg>

                      {/* Role with Icon */}
                      <div className="flex items-center gap-2.5 min-w-[180px] sm:min-w-[210px] shrink-0 text-left">
                        <layer.roleIcon className="w-4 h-4 text-white/90 shrink-0" strokeWidth={2} />
                        <span className="text-[12px] sm:text-[12.5px] font-medium text-white/90 tracking-wide">
                          {layer.role}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Key Benefits Section matching Screenshots 1 & 2 */}
      <section
        className="w-full py-16 sm:py-20 lg:py-24 border-b border-[#E2E8F0]"
        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: Title on Left, Description on Right */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 lg:mb-14">
            <div className="flex flex-col items-start">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                KEY BENEFITS
              </p>
              <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[40px] font-extrabold leading-[1.18] tracking-tight text-[#0F172A]">
                <span className="block whitespace-normal sm:whitespace-nowrap">
                  Drive productivity, efficiency and
                </span>
                <span className="block text-[#00D2EE]">
                  innovation.
                </span>
              </h2>
            </div>

            <div className="border-l border-[#CBD5E1] pl-5 sm:pl-6 py-1 md:mb-1.5 shrink-0">
              <p className="text-[13.5px] sm:text-[14px] leading-[21px] sm:leading-[22px] text-[#64748B] whitespace-normal sm:whitespace-nowrap">
                Discover the value that intelligent manufacturing<br className="hidden sm:block" />
                automation brings to your enterprise.
              </p>
            </div>
          </div>

          {/* Cards Grid: 3 columns x 2 rows, matching Screenshot 2 specs:
              Width Fixed (411px), Height Hug (235px), Radius 16px, Border 1px rgba(226, 232, 240, 1),
              Padding 20px, Gap 10px, Background rgba(255, 255, 255, 1) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-[26px]">
            {KEY_BENEFITS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex flex-col justify-start rounded-[16px] bg-white transition-all duration-300 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 select-none"
                  style={{
                    width: "100%",
                    maxWidth: "411px",
                    minHeight: "220px",
                    border: "1px solid rgba(226, 232, 240, 1)",
                    padding: "20px",
                    gap: "10px",
                  }}
                >
                  {/* Icon */}
                  <div className="flex items-center justify-start">
                    <Icon
                      className="w-8 h-8 sm:w-9 sm:h-9"
                      style={{ color: item.iconColor }}
                      strokeWidth={2.2}
                    />
                  </div>

                  {/* Title */}
                  <h3 className="mt-2 text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Real Business Impact Section matching Screenshots 1, 2 & 3 */}
      <section
        className="w-full py-16 sm:py-20 lg:py-[96px] border-t"
        style={{
          backgroundColor: "rgba(236, 245, 253, 1)",
          borderTopColor: "rgba(241, 245, 249, 1)",
          borderTopWidth: "1px",
        }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: Title on Left, Description on Right */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-14">
            <div className="flex flex-col items-start max-w-[720px]">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                REAL BUSINESS IMPACT
              </p>
              <h2 className="mt-4 text-[32px] sm:text-[38px] lg:text-[42px] font-extrabold leading-[1.18] tracking-tight text-[#0F172A]">
                <span className="block sm:inline whitespace-normal lg:whitespace-nowrap">
                  Measurable results for <span className="text-[#2563EB]">modern</span>
                </span>
                <br className="hidden sm:block" />
                <span className="text-[#00C2FF]">manufacturers.</span>
              </h2>
            </div>

            <p className="text-[14px] sm:text-[14.5px] leading-[23px] text-[#64748B] max-w-[430px] pb-1">
              Our automation stacks help manufacturers reduce costs, improve quality and increase operational efficiency.
            </p>
          </div>

          {/* Cards Grid: 4 horizontal cards matching Screenshot 3 specs:
              Flow: Horizontal, Width: Fill (302px), Height: Hug (98px), Radius: 16px,
              Border: 1px rgba(218, 218, 218, 1), Padding: 24px, Gap: 16px, Background: rgba(255, 255, 255, 1) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {IMPACT_STATS.map((item) => (
              <div
                key={item.stat}
                className="w-full flex flex-row items-center bg-white transition-all duration-300 hover:shadow-[0_10px_25px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 select-none"
                style={{
                  minHeight: "98px",
                  borderRadius: "16px",
                  border: "1px solid rgba(218, 218, 218, 1)",
                  padding: "24px",
                  gap: "16px",
                  backgroundColor: "rgba(255, 255, 255, 1)",
                }}
              >
                {/* Icon Badge */}
                {item.icon}

                {/* Stat + Label */}
                <div className="flex flex-col justify-center min-w-0">
                  <div className="text-[22px] sm:text-[24px] font-extrabold text-[#0F172A] tracking-tight leading-none">
                    {item.stat}
                  </div>
                  <div className="text-[12px] sm:text-[12.5px] text-[#64748B] font-medium leading-tight mt-1 whitespace-nowrap">
                    {item.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Story Section matching Screenshots 1, 2, 3 & 4 */}
      <section
        className="w-full py-20 sm:py-24 lg:py-[100px] border-t border-white/[0.06]"
        style={{ backgroundColor: "rgba(5, 12, 24, 1)" }}
      >
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
            {/* Left Content (Columns 1-6) */}
            <div className="lg:col-span-6 flex flex-col items-start max-w-[560px]">
              <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
                SUCCESS STORY
              </p>
              <h2 className="mt-4 text-[34px] sm:text-[40px] lg:text-[44px] font-extrabold text-white leading-[1.15] tracking-tight">
                <span className="block sm:whitespace-nowrap">Transforming Manufacturing</span>
                <span className="block text-white">Operations</span>
              </h2>
              <p className="mt-6 text-[14.5px] sm:text-[15px] leading-[26px] text-[#94A3B8]">
                A global electronics manufacturer partnered with Simpel to unify their
                production planning, inventory, quality and maintenance processes. The result
                was higher production efficiency, reduced downtime and better visibility
                across multiple plants.
              </p>
            </div>

            {/* Right Media (Columns 7-12) matching Screenshot 4:
                Flow: Vertical, Width: Fill (633px), Height: Hug (370px), Radius: 16px,
                Border: 1px rgba(51, 65, 85, 1), Drop shadow: 0 25px 50px rgba(0, 0, 0, 0.25) */}
            <div className="lg:col-span-6 flex justify-center lg:justify-end">
              <div
                className="relative w-full max-w-[633px] h-[340px] sm:h-[370px] rounded-[16px] overflow-hidden select-none"
                style={{
                  border: "1px solid rgba(51, 65, 85, 1)",
                  backgroundColor: "rgba(12, 26, 48, 1)",
                  boxShadow: "0px 25px 50px 0px rgba(0, 0, 0, 0.25)",
                }}
              >
                {/* Background image */}
                <img
                  src="/images/what-we-do/manufacturing-success-story.jpg"
                  alt="Transforming Manufacturing Operations"
                  className="w-full h-full object-cover"
                />

                {/* Subtle dark gradient overlay to give depth and contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />

                {/* Overlay Card matching Screenshot 2 (centered horizontally and vertically): */}
                <div
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-32px)] sm:w-[384px] max-w-[384px] rounded-[16px] flex flex-col justify-between"
                  style={{
                    backgroundColor: "rgba(11, 25, 46, 0.75)",
                    border: "1px solid rgba(0, 210, 255, 0.3)",
                    padding: "20px",
                    gap: "16px",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  {SUCCESS_METRICS.map((metric) => (
                    <div key={metric.label} className="flex flex-col gap-2">
                      <div className="flex items-center justify-between text-[13px] sm:text-[13.5px]">
                        <span className="font-semibold text-white tracking-wide">
                          {metric.label}
                        </span>
                        <span className="font-bold text-[#00D2EE]">
                          {metric.value}
                        </span>
                      </div>
                      <div className="w-full h-[6px] rounded-full bg-[#1E293B]/80 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[#00D2EE] transition-all duration-700 ease-out"
                          style={{ width: `${metric.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by Manufacturing Leaders Section with Marquee */}
      <style>{`
        @keyframes marqueeManufacturing {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee-manufacturing {
          display: flex;
          width: max-content;
          animation: marqueeManufacturing 22s linear infinite;
        }
        .animate-marquee-manufacturing:hover {
          animation-play-state: paused;
        }
      `}</style>
      <section className="w-full bg-white py-16 sm:py-20 lg:py-24 border-t border-[#F1F5F9] overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px] text-center mb-12 sm:mb-14">
          <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-extrabold text-[#0F172A] tracking-tight">
            Trusted by Manufacturing Leaders
          </h2>
          <p className="mt-3 text-[14px] sm:text-[15px] text-[#64748B] max-w-[560px] mx-auto">
            Our clients achieve measurable improvements and scalable growth.
          </p>
        </div>

        {/* Marquee Row */}
        <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
          <div className="animate-marquee-manufacturing items-center py-2">
            {[...MANUFACTURING_PARTNERS, ...MANUFACTURING_PARTNERS, ...MANUFACTURING_PARTNERS, ...MANUFACTURING_PARTNERS].map((partner, idx) => (
              <div
                key={`${partner.name}-${idx}`}
                className={`flex shrink-0 items-center justify-center px-8 sm:px-10 lg:px-12 select-none ${
                  partner.flow === "vertical" ? "flex-col" : "flex-row"
                }`}
                style={partner.gap ? { gap: partner.gap } : undefined}
              >
                <img
                  src={partner.img}
                  alt={partner.name}
                  style={{
                    width: partner.width,
                    height: partner.height,
                  }}
                  className="object-contain opacity-90 transition-all duration-300 hover:opacity-100 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Transform CTA Section */}
      <section className="relative w-full overflow-hidden bg-[#020A15] py-20 sm:py-24 lg:py-28 min-h-[440px] flex items-center">
        {/* Background Earth Glow Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/what-we-do/manufacturing-cta-earth.png"
            alt="Ready to transform your manufacturing"
            className="w-full h-full object-cover object-[70%_center] lg:object-right select-none pointer-events-none"
          />
          {/* Subtle mobile/tablet readability vignette */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#020A15]/80 via-[#020A15]/40 to-transparent lg:hidden" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="max-w-[680px]">
            {/* Eyebrow */}
            <p className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
              READY TO TRANSFORM YOUR MANUFACTURING
            </p>

            {/* Heading */}
            <h2 className="mt-4 text-[34px] sm:text-[42px] lg:text-[46px] font-extrabold text-white leading-[1.15] tracking-tight">
              <span className="block sm:whitespace-nowrap">Let’s build an intelligent factory,</span>
              <span className="block">together.</span>
            </h2>

            {/* Paragraph */}
            <p className="mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#94A3B8] max-w-[480px]">
              Talk to our team to explore how our Manufacturing Automation Stacks can help your business grow.
            </p>

            {/* CTA Button */}
            <div className="mt-8">
              <button
                onClick={openAgent}
                className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-semibold text-[#0F172A] shadow-[0_0_35px_rgba(0,180,255,0.45)] transition-all duration-300 hover:bg-slate-100 hover:shadow-[0_0_45px_rgba(0,180,255,0.65)] hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>Talk to Our Team</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default ManufacturingStackPage;
